// Hero "1 producto → sus resultados" para las pantallas de selección de
// producto de los modos catálogo/infografía. Replica el collage del modo
// automático (la del polo): foto del producto abajo-izquierda, flecha azul
// curva, y los resultados generados apilados en abanico arriba-derecha.
//
// Es un COMPONENTE (no un PNG baked como los otros heroes) por dos razones:
// queda nítido en cualquier DPI y se puede reencuadrar sin reexportar, y así
// referencia directo los assets que se dropean en /public/images. Ver ADR-817.
//
// Dos layouts según la forma del producto (afinados a ojo con las imágenes
// reales de cada modo):
//   • 'fan3-wide'  → 3 resultados, cards landscape. Para productos ANCHOS
//                    (ej. la mesa ratona de infografía).
//   • 'fan2-tall'  → 2 resultados, cards portrait. Para productos ALTOS
//                    (ej. el ropero de catálogo).

import React from 'react';

export interface ProductHeroCollageProps {
  /** Foto del producto original (el input). Va abajo-izquierda. */
  original: string;
  /** Resultados generados (apilados en abanico). */
  results: string[];
  /** Composición según la forma del producto. Default 'fan3-wide'. */
  layout?: 'fan3-wide' | 'fan2-tall';
  className?: string;
}

/** Card de imagen del collage (misma cáscara para todas): esquinas redondeadas,
 *  sombra, ring finito, object-cover para que el producto llene la card. */
const CollageCard: React.FC<{ src: string; className: string }> = ({
  src,
  className,
}) => (
  <figure
    className={`absolute rounded-2xl overflow-hidden bg-white ring-1 ring-black/[0.07] m-0 ${className}`}
  >
    <img src={src} alt="" className="w-full h-full object-cover" />
  </figure>
);

/** Flecha azul curva. `d` y posición se pasan por layout. */
const Arrow: React.FC<{ className: string; d: string; head: string }> = ({
  className,
  d,
  head,
}) => (
  <svg
    className={`absolute h-auto z-20 ${className}`}
    viewBox="0 0 150 100"
    fill="none"
    aria-hidden
  >
    <path d={d} stroke="#2563EB" strokeWidth="8" strokeLinecap="round" />
    <path
      d={head}
      stroke="#2563EB"
      strokeWidth="8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ProductHeroCollage: React.FC<ProductHeroCollageProps> = ({
  original,
  results,
  layout = 'fan3-wide',
  className = '',
}) => {
  const cards = (results ?? []).slice(0, 3);

  if (layout === 'fan2-tall') {
    // Producto alto (ropero): original + 2 resultados en cards portrait.
    return (
      <div className={`relative w-full max-w-[480px] aspect-[5/4] ${className}`}>
        {cards[0] && (
          <CollageCard
            src={cards[0]}
            className="top-[2%] left-[41%] w-[31%] aspect-[4/5] shadow-lg -rotate-3 z-10"
          />
        )}
        {cards[1] && (
          <CollageCard
            src={cards[1]}
            className="top-[24%] right-[1%] w-[38%] aspect-[3/4] shadow-xl rotate-1 z-30"
          />
        )}
        <Arrow
          className="left-[29%] top-[52%] w-[24%]"
          d="M4 88 C 50 104, 108 88, 140 26"
          head="M119 22 L144 24 L134 50"
        />
        <CollageCard
          src={original}
          className="bottom-[5%] left-[2%] w-[30%] aspect-[4/5] shadow-xl z-20"
        />
      </div>
    );
  }

  // 'fan3-wide' — producto ancho (mesa): original + 3 resultados landscape, con
  // tamaños distintos y stagger (como el polo).
  return (
    <div className={`relative w-full max-w-[480px] aspect-[5/4] ${className}`}>
      {cards[0] && (
        <CollageCard
          src={cards[0]}
          className="top-[6%] left-[35%] w-[31%] aspect-[4/3] shadow-lg -rotate-3 z-10"
        />
      )}
      {cards[1] && (
        <CollageCard
          src={cards[1]}
          className="top-[0%] right-[0%] w-[38%] aspect-[4/3] shadow-lg rotate-2 z-10"
        />
      )}
      {cards[2] && (
        <CollageCard
          src={cards[2]}
          className="top-[40%] right-[6%] w-[34%] aspect-[4/3] shadow-xl z-30"
        />
      )}
      <Arrow
        className="left-[30%] top-[50%] w-[26%]"
        d="M4 88 C 50 104, 110 86, 142 22"
        head="M121 18 L146 18 L138 46"
      />
      <CollageCard
        src={original}
        className="bottom-[4%] left-0 w-[33%] aspect-[4/5] shadow-xl z-20"
      />
    </div>
  );
};

export default ProductHeroCollage;
